<template>
  <div class="page-builder-widgets" :data-editable="isEditable">
    <draggable
      v-model="widgetsList"
      item-key="id"
      :group="{ name: 'widgets', pull: true, put: ['widget-library', 'widgets', 'nested-widgets'] }"
      :disabled="!isEditable"
      @change="onWidgetsChange"
      class="widgets-container"
    >
      <template #item="{ element: widget, index }">
        <div 
          class="widget-item"
          :class="[
            { 'widget-selected': selectedWidgetId === widget.id },
            widget.entranceAnimation && widget.entranceAnimation !== 'none' ? `animate-${widget.entranceAnimation}` : ''
          ]"
          :style="{
            animationDuration: widget.animationDuration || '0.6s',
            animationDelay: widget.animationDelay || '0s'
          }"
          :data-hidden-mobile="widget.hidden_on_mobile || false"
          @click="handleWidgetItemClick(widget.id, $event)"
        >
          <!-- Widget Content -->
          <div class="widget-content">
            <TextWidget v-if="widget.type === 'text'" :widget="widget" />
            <EnhancedTextWidget v-else-if="widget.type === 'enhanced-text'" :widget="widget" />
            <ButtonWidget v-else-if="widget.type === 'button'" :widget="widget" />
            <ImageWidget v-else-if="widget.type === 'image'" :widget="widget" />
            <SpacerWidget v-else-if="widget.type === 'spacer'" :widget="widget" />
            <DividerWidget v-else-if="widget.type === 'divider'" :widget="widget" />
            <PostCardsWidget v-else-if="widget.type === 'post-cards'" :widget="widget" />
            <ProductCardsWidget v-else-if="widget.type === 'product-cards'" :widget="widget" />
            <TourCardsWidget v-else-if="widget.type === 'tour-cards'" :widget="widget" />
            <VideoWidget v-else-if="widget.type === 'video'" :widget="widget" />
            <GalleryWidget v-else-if="widget.type === 'gallery'" :widget="widget" />
            <HtmlWidget v-else-if="widget.type === 'html'" :widget="widget" />
            <ContactFormWidget v-else-if="widget.type === 'contact-form'" :widget="widget" />
            <AccordionWidget v-else-if="widget.type === 'accordion'" :widget="widget" />
            <TabsWidget v-else-if="widget.type === 'tabs'" :widget="widget" />
            <ProgressBarWidget v-else-if="widget.type === 'progress-bar'" :widget="widget" />
            <CounterWidget v-else-if="widget.type === 'counter'" :widget="widget" />
            <SocialMediaWidget v-else-if="widget.type === 'social-media'" :widget="widget" />
            <FacebookPageWidget v-else-if="widget.type === 'facebook-page'" :widget="widget" />
            <ReviewButtonWidget v-else-if="widget.type === 'review-button'" :widget="widget" />
            <ReviewsDisplayWidget v-else-if="widget.type === 'reviews-display'" :widget="widget" />
            <TwoColumnsWidget 
              v-else-if="widget.type === 'two-columns'" 
              :widget="widget" 
              :is-editable="isEditable"
              :modal-open="modalOpen"
              @update="updateLayoutWidget(index, $event)"
              @edit-child="editWidget"
            />
            <ThreeColumnsWidget 
              v-else-if="widget.type === 'three-columns'" 
              :widget="widget" 
              :is-editable="isEditable"
              :modal-open="modalOpen"
              @update="updateLayoutWidget(index, $event)"
              @edit-child="editWidget"
            />
            <FourColumnsWidget 
              v-else-if="widget.type === 'four-columns'" 
              :widget="widget" 
              :is-editable="isEditable"
              :modal-open="modalOpen"
              @update="updateLayoutWidget(index, $event)"
              @edit-child="editWidget"
            />
            <div v-else class="unknown-widget">
              <i class="bi bi-question-circle"></i>
              Unknown widget type: {{ widget.type }}
            </div>
          </div>
          
          <!-- Widget Controls (shown on hover/select) -->
          <div class="widget-controls" v-if="isEditable && !modalOpen">
            <div class="widget-controls-bar">
              <button 
                type="button"
                class="btn btn-sm btn-outline-primary"
                @mousedown.stop
                @pointerdown.stop
                @click.stop="editWidget(widget)"
                title="Επεξεργασία"
              >
                <i class="bi bi-pencil"></i>
              </button>
              <button 
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @mousedown.stop
                @pointerdown.stop
                @click.stop="duplicateWidget(index)"
                title="Αντιγραφή"
              >
                <i class="bi bi-files"></i>
              </button>
              <button 
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @mousedown.stop
                @pointerdown.stop
                @click.stop="moveWidget(index, -1)"
                title="Μετακίνηση πάνω"
                :disabled="index === 0"
              >
                <i class="bi bi-arrow-up"></i>
              </button>
              <button 
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @mousedown.stop
                @pointerdown.stop
                @click.stop="moveWidget(index, 1)"
                title="Μετακίνηση κάτω"
                :disabled="index === widgetsList.length - 1"
              >
                <i class="bi bi-arrow-down"></i>
              </button>
              <button 
                type="button"
                class="btn btn-sm btn-outline-danger"
                @mousedown.stop
                @pointerdown.stop
                @click.stop="removeWidget(index)"
                title="Διαγραφή"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </template>
    </draggable>
    
    <!-- Empty State -->
    <div v-if="widgetsList.length === 0" class="empty-widgets-state">
      <i class="bi bi-plus-square fs-1 text-muted mb-3"></i>
      <h6 class="text-muted">Δεν υπάρχουν widgets</h6>
      <p class="text-muted small">Σύρετε widgets από τη βιβλιοθήκη για να ξεκινήσετε</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
// @ts-ignore
import draggable from 'vuedraggable'
import type { Widget } from '@/types/pagebuilder'

// Import widgets
import TextWidget from './TextWidget.vue'
import EnhancedTextWidget from './EnhancedTextWidget.vue'
import ButtonWidget from './ButtonWidget.vue'
import ImageWidget from './ImageWidget.vue'
import SpacerWidget from './SpacerWidget.vue'
import DividerWidget from './DividerWidget.vue'
import TwoColumnsWidget from './TwoColumnsWidget.vue'
import ThreeColumnsWidget from './ThreeColumnsWidget.vue'
import FourColumnsWidget from './FourColumnsWidget.vue'
import PostCardsWidget from './PostCardsWidget.vue'
import ProductCardsWidget from './ProductCardsWidget.vue'
import TourCardsWidget from './TourCardsWidget.vue'
import VideoWidget from './VideoWidget.vue'
import GalleryWidget from './GalleryWidget.vue'
import HtmlWidget from './HtmlWidget.vue'
import ContactFormWidget from './ContactFormWidget.vue'
import AccordionWidget from './AccordionWidget.vue'
import TabsWidget from './TabsWidget.vue'
import CounterWidget from './CounterWidget.vue'
import ProgressBarWidget from './ProgressBarWidget.vue'
import SocialMediaWidget from './SocialMediaWidget.vue'
import FacebookPageWidget from './FacebookPageWidget.vue'
import ReviewButtonWidget from '@/components/widgets/ReviewButtonWidget.vue'
import ReviewsDisplayWidget from '@/components/widgets/ReviewsDisplayWidget.vue'

interface Props {
  widgets: Widget[]
  isEditable?: boolean
  modalOpen?: boolean
}

interface Emits {
  (e: 'update:widgets', widgets: Widget[]): void
  (e: 'widget-selected', widgetId: string): void
  (e: 'widget-edit', widget: Widget): void
}

const props = withDefaults(defineProps<Props>(), {
  isEditable: true,
  modalOpen: false
})

const emit = defineEmits<Emits>()

const selectedWidgetId = ref<string | null>(null)

const widgetsList = computed({
  get: () => props.widgets,
  set: (value) => emit('update:widgets', value)
})

const selectWidget = (widgetId: string) => {
  selectedWidgetId.value = widgetId
  emit('widget-selected', widgetId)
}

const handleWidgetItemClick = (widgetId: string, event: MouseEvent) => {
  if (!props.isEditable) {
    return
  }

  const target = event.target as HTMLElement | null
  if (
    target?.closest(
      'a, button, input, select, textarea, label, [role="button"], [contenteditable="true"]',
    )
  ) {
    return
  }

  selectWidget(widgetId)
}

const editWidget = (widget: Widget) => {
  selectWidget(widget.id)
  emit('widget-edit', widget)
}

const duplicateWidget = (index: number) => {
  const widget = widgetsList.value[index]
  const newWidget = {
    ...widget,
    id: `${Date.now()}-${Math.random()}`,
    order: widget.order + 0.1
  }
  
  const newWidgets = [...widgetsList.value]
  newWidgets.splice(index + 1, 0, newWidget)
  
  // Re-order widgets
  newWidgets.forEach((w, i) => {
    w.order = i
  })
  
  emit('update:widgets', newWidgets)
}

const moveWidget = (index: number, direction: -1 | 1) => {
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= widgetsList.value.length) return
  
  const newWidgets = [...widgetsList.value]
  const widget = newWidgets.splice(index, 1)[0]
  newWidgets.splice(newIndex, 0, widget)
  
  // Re-order widgets
  newWidgets.forEach((w, i) => {
    w.order = i
  })
  
  emit('update:widgets', newWidgets)
}

const removeWidget = (index: number) => {
  const newWidgets = widgetsList.value.filter((_, i) => i !== index)
  newWidgets.forEach((w, i) => {
    w.order = i
  })
  emit('update:widgets', newWidgets)
  selectedWidgetId.value = null
}

const updateLayoutWidget = (index: number, updatedWidget: Widget) => {
  const newWidgets = widgetsList.value.map((widget, i) =>
    i === index ? { ...updatedWidget } : widget,
  )
  emit('update:widgets', newWidgets)
}

const onWidgetsChange = () => {
  // Update order when widgets are dragged
  widgetsList.value.forEach((widget, index) => {
    widget.order = index
  })
}
</script>

<style scoped>
@import '../../../assets/css/project-cards.css';
@import '../../../assets/widget-animations.css';
.page-builder-widgets {
  min-height: 200px;
}

.widgets-container {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.widget-item {
  position: relative;
  margin-bottom: 0;
  padding: 0;
  border-radius: 10px;
}

/* Edit mode styles - only when editable */
.page-builder-widgets[data-editable="true"] .widget-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: transparent;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: visible;
}

.page-builder-widgets[data-editable="true"] .widget-item:hover {
  border-color: #2563eb;
  background-color: #f8fbff;
}

.page-builder-widgets[data-editable="true"] .widget-selected {
  border-color: #2563eb !important;
  background-color: #f8fbff !important;
  box-shadow: 0 0 0 1px #2563eb;
}

/* Widget content interaction */
.page-builder-widgets[data-editable="true"] .widget-content {
  pointer-events: none; /* Prevent interaction in edit mode */
}

.page-builder-widgets[data-editable="false"] .widget-content {
  pointer-events: auto;
}

.page-builder-widgets[data-editable="false"] .widget-item {
  cursor: default;
}

/* Widget controls - only in edit mode */
.page-builder-widgets[data-editable="true"] .widget-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: auto;
}

.page-builder-widgets[data-editable="true"] .widget-item:hover .widget-controls,
.page-builder-widgets[data-editable="true"] .widget-selected .widget-controls {
  opacity: 1;
}

.widget-controls-bar {
  display: flex;
  gap: 2px;
  background: white;
  border-radius: 6px;
  padding: 2px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.widget-controls-bar .btn {
  padding: 4px 8px;
  font-size: 0.75rem;
}

.empty-widgets-state {
  text-align: center;
  padding: 3rem 1rem;
  border: 2px dashed #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.unknown-widget {
  padding: 1rem;
  text-align: center;
  color: #6c757d;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

/* Draggable styles */
.sortable-ghost {
  opacity: 0.5;
}

.sortable-drag {
  transform: rotate(5deg);
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
}

/* Hide widgets marked as hidden_on_mobile on mobile devices */
@media (max-width: 768px) {
  .widget-item[data-hidden-mobile="true"] {
    display: none !important;
  }
}
</style>